import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { getTranslate } from 'react-localize-redux';

import { List, RadioButton } from '../../../../../../components/atoms';
import { ListItem } from '../../../../../../components/atoms/List';
import { RadioGroup } from '../../../../../../components/layouts';
import { analyticsService } from '../../../../../../core/services';
import { paymentSelectors } from '../../../../../../weborder/selectors';
import { paymentOptionsUtils } from '../../utils';
import { DispatchProps } from './index';

type PaymentMethodSelectorProps = {
  disabled?: boolean;
  paymentOptions: ReturnType<typeof paymentSelectors.getAvailablePayments>;
  selectedPaymentId?: string;
  translate: ReturnType<typeof getTranslate>;
};
type Props = PaymentMethodSelectorProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  paymentOptions: {
    '&.fd-list': {
      paddingBottom: 0,
    },

    '& .fd-list__item': {
      padding: 0,
    },
  },

  paymentOption: {
    padding: theme.spacing(1.75, 0),
  },

  paymentOptionDescription: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    marginLeft: theme.spacing(0.5),

    '& .fd-icon': {
      marginRight: theme.spacing(2),
    },
  },
}));

export function PaymentOptions(props: Props): JSX.Element {
  const {
    disabled,
    paymentOptions,
    selectedPaymentId,
    selectPayment,
    translate,
  } = props;

  const classes = useStyles();
  const renderPaymentOption = (payment): JSX.Element => {
    const { icon, fdKey, text } = paymentOptionsUtils.getPaymentOptionsDetails(
      payment,
      translate,
    );

    const optionLabel = (
      <div className={classes.paymentOptionDescription}>
        {icon} {text}
      </div>
    );

    return (
      <RadioButton
        checked={payment.paymentId === selectedPaymentId}
        className={classes.paymentOption}
        disabled={disabled || payment.status !== 'success'}
        key={`payment-option-${payment.paymentId}`}
        label={optionLabel}
        testId={fdKey}
        value={payment.paymentId}
      />
    );
  };

  const optionsMapped: ListItem[] = paymentOptions.map((option) => ({
    key: option.paymentId,
    primaryContent: renderPaymentOption(option),
  }));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const paymentMethod = paymentOptions.find(
      (option) => option.paymentId === event.target.value,
    );
    const defaultPaymentMethod = paymentOptions.find(
      (option) => option.IsDefaultPaymentMethod === true,
    );
    const paymentMethodDetails = paymentMethod?.Description.startsWith('Card')
      ? 'Card'
      : paymentMethod?.Description;
    const paymentData = {
      amp_defaultPaymentMethod: defaultPaymentMethod?.Description,
      amp_label: 'Payment Method Selected',
      amp_paymentMethod: paymentMethodDetails,
    };
    analyticsService.track(paymentData);
    selectPayment(event.target.value);
  };
  return (
    <RadioGroup onChange={handleChange} value={selectedPaymentId}>
      <List className={classes.paymentOptions} items={optionsMapped} />
    </RadioGroup>
  );
}
